import React from 'react'
import { useWatch } from 'react-hook-form'
import { FormField } from '../forms/FormField'
import type { FnaFormValues } from '../../contracts/fnaFormSchema'

/**
 * Step 4: Non-retirement savings, other assets, and monthly savings cadence.
 *
 * Spouse- and joint-named fields are conditional on `withSpouse === 'yes'`
 * so a single filer never sees them. Includes a bug fix for `jointRetirement`
 * which was previously shown unconditionally.
 *
 * @returns JSX.Element The savings step
 */
export function SavingsStep(): JSX.Element {
  const withSpouse = useWatch<FnaFormValues, 'withSpouse'>({ name: 'withSpouse' })
  return (
    <>
      <h2>Savings &amp; Assets</h2>
      <FormField<FnaFormValues>
        name="totalNonRetirementSavings"
        label="Total regular savings (not for retirement)"
        type="number"
        placeholder="0"
        inputMode="decimal"
      />
      {/*
        The per-person retirement balances and the per-person regular-savings
        breakdown are only meaningful when there is a spouse to split against -
        for a single filer, "your retirement balance" equals totalRetirementSavings
        and "your regular savings" equals totalNonRetirementSavings. Gating both
        breakdowns on withSpouse === 'yes' mirrors the LLM conversation flow at
        conversation_manager.py:140-143 and avoids asking the same question twice.
      */}
      {withSpouse === 'yes' && (
        <>
          <h3 style={{ color: 'var(--accent-cyan)', marginTop: '1.5rem' }}>Retirement balance breakdown</h3>
          <FormField<FnaFormValues>
            name="clientRetirement"
            label="Your share of retirement savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
          <FormField<FnaFormValues>
            name="spouseRetirement"
            label="Spouse's share of retirement savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
          <FormField<FnaFormValues>
            name="jointRetirement"
            label="Joint retirement balance"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
        </>
      )}
      <FormField<FnaFormValues>
        name="otherAssets"
        label="Other assets (vehicles, real estate, collectibles, etc.)"
        type="number"
        placeholder="0"
        inputMode="decimal"
      />

      {withSpouse === 'yes' && (
        <>
          <h3 style={{ color: 'var(--accent-cyan)', marginTop: '1.5rem' }}>Regular savings breakdown</h3>
          <FormField<FnaFormValues>
            name="clientNonRetirementSavings"
            label="Your share of regular savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
          <FormField<FnaFormValues>
            name="spouseNonRetirementSavings"
            label="Spouse's share of regular savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
          <FormField<FnaFormValues>
            name="jointNonRetirementSavings"
            label="Joint regular savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
        </>
      )}

      <h3 style={{ color: 'var(--accent-cyan)', marginTop: '1.5rem' }}>Monthly contributions to regular savings</h3>
      <FormField<FnaFormValues>
        name="clientMonthlySavings"
        label={
          withSpouse === 'yes'
            ? "Your monthly contribution to regular savings"
            : 'Monthly contribution to regular savings'
        }
        type="number"
        placeholder="0"
        inputMode="decimal"
      />
      {withSpouse === 'yes' && (
        <>
          <FormField<FnaFormValues>
            name="spouseMonthlySavings"
            label="Spouse's monthly contribution to regular savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
          <FormField<FnaFormValues>
            name="jointMonthlySavings"
            label="Joint monthly contribution to regular savings"
            type="number"
            placeholder="0"
            inputMode="decimal"
          />
        </>
      )}
    </>
  )
}
